import { GetDataDynamicKey } from '@erc725/erc725.js/build/main/src/types/GetData'
import LSP4DigitalAssetSchema from '@erc725/erc725.js/schemas/LSP4DigitalAsset.json'
import LSP3Schema from '@erc725/erc725.js/schemas/LSP3ProfileMetadata.json'
import ERC725, { ERC725JSONSchema } from '@erc725/erc725.js'
import { LSP4DigitalAssetMetadata } from '@lukso/lsp4-contracts'
import { LSP3ProfileMetadata } from '@lukso/lsp3-contracts'

import {
  IPFS_URL,
  LSP3_PROFILE,
  LSP4_METADATA,
  LSP4_TOKEN_NAME,
  LSP4_TOKEN_SYMBOL,
} from '@/shared/config'
import LSP8IdentifiableDigitalAssetSchema from '@/shared/schemas/LSP8IdentifiableDigitalAssetSchema.json'
import { useGlobals } from '@/popup/compositions/useGlobals'
import { assertNonEmptyString } from '@/shared/validators'
import { logWarn } from '@/shared/logger'
import { TokenIdType } from '@/shared/models/Nft'
import { getDataABI } from '@/shared/abis/getDataABI'
import { useWeb3Eth } from '@/popup/compositions/useWeb3Eth'

const { createContract } = useWeb3Eth()

const getErc725 = (
  address: string,
  schemas = LSP3Schema as ERC725JSONSchema[]
): ERC725 => {
  const { ethProvider } = useGlobals()

  return new ERC725(schemas, address, ethProvider, {
    ipfsGateway: IPFS_URL,
  })
}

const fetchProfile = async (address: string) => {
  const erc725 = getErc725(address)
  const profile = await erc725.fetchData(LSP3_PROFILE)
  return (profile?.value as unknown as { LSP3Profile: LSP3ProfileMetadata })
    ?.LSP3Profile
}

const fetchDigitalAsset = async (assetAddress: string) => {
  const erc725 = getErc725(
    assetAddress,
    LSP4DigitalAssetSchema as ERC725JSONSchema[]
  )
  let name: string | undefined
  let symbol: string | undefined
  let metaData: LSP4DigitalAssetMetadata | undefined

  // each of these fetchData can fail, so we need to catch them separately

  try {
    const { value: fetchData } = await erc725.fetchData(LSP4_TOKEN_NAME)
    assertNonEmptyString(fetchData, 'Invalid token name format')
    name = fetchData
  } catch (error) {
    logWarn(error)
  }

  try {
    const { value: fetchData } = await erc725.fetchData(LSP4_TOKEN_SYMBOL)
    assertNonEmptyString(fetchData, 'Invalid token symbol format')
    symbol = fetchData
  } catch (error) {
    logWarn(error)
  }

  try {
    const { value: fetchData } = await erc725.fetchData(LSP4_METADATA)
    metaData = (
      fetchData as unknown as {
        LSP4Metadata: LSP4DigitalAssetMetadata
      }
    )?.LSP4Metadata
  } catch (error) {
    logWarn(error)
  }
  return {
    name,
    symbol,
    metaData,
  }
}

const fetchLsp8TokenMetadata = async (
  assetAddress: string,
  tokenId: string,
  tokenIdType: TokenIdType
): Promise<LSP4DigitalAssetMetadata> => {
  const erc725 = getErc725(
    assetAddress,
    LSP8IdentifiableDigitalAssetSchema.concat(
      LSP4DigitalAssetSchema
    ) as ERC725JSONSchema[]
  )

  let tokenMetadataKey: GetDataDynamicKey
  let metadata: LSP4DigitalAssetMetadata | undefined

  switch (tokenIdType) {
    case TokenIdType.address: {
      tokenMetadataKey = {
        keyName: 'LSP4Metadata:<address>',
        dynamicKeyParts: tokenId.slice(0, 42),
      }
      break
    }
    case TokenIdType.uint256: {
      tokenMetadataKey = {
        keyName: 'LSP4Metadata:<uint256>',
        dynamicKeyParts: Number.parseInt(tokenId.slice(2), 16).toString(),
      }
      break
    }
    default: {
      // When no tokenIdType, we assume it's a bytes32 type
      tokenMetadataKey = {
        keyName: 'LSP4Metadata:<bytes32>',
        dynamicKeyParts: tokenId,
      }
      break
    }
  }

  try {
    const _metadata = await erc725.fetchData(tokenMetadataKey)
    metadata = (
      _metadata.value as unknown as {
        LSP4Metadata: LSP4DigitalAssetMetadata
      }
    )?.LSP4Metadata
  } catch (error) {
    logWarn(error)
    throw new Error('Unable to fetch lsp4 metadata')
  }

  return metadata
}

const fetchLSP4Creator = async (
  assetAddress: string
): Promise<
  { address: string; lsp3Profile: LSP3ProfileMetadata } | undefined
> => {
  try {
    const creator = await createContract(getDataABI, assetAddress)
      .methods['getData(bytes32)'](
        '0x114bd03b3a46d48759680d81ebb2b41400000000000000000000000000000000'
      )
      .call()
    return creator
      ? { address: creator, lsp3Profile: await fetchProfile(creator) }
      : undefined
  } catch {
    return undefined
  }
}

export default function useErc725(): {
  getErc725: (address: string, schemas?: ERC725JSONSchema[]) => ERC725
  fetchProfile: (address: string) => Promise<LSP3ProfileMetadata>
  fetchDigitalAsset: (assetAddress: string) => Promise<{
    name?: string
    symbol?: string
    metaData?: LSP4DigitalAssetMetadata
  }>
  fetchLsp8TokenMetadata: (
    address: string,
    tokenId: string,
    tokenIdType: TokenIdType
  ) => Promise<LSP4DigitalAssetMetadata>
  fetchLSP4Creator: (
    assetAddress: string
  ) => Promise<
    { address: string; lsp3Profile: LSP3ProfileMetadata } | undefined
  >
} {
  return {
    getErc725,
    fetchProfile,
    fetchDigitalAsset,
    fetchLsp8TokenMetadata,
    fetchLSP4Creator,
  }
}
